import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper,
  Typography,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
  Badge,
} from "@material-ui/core";
import {
  Chat as ChatIcon,
  FiberManualRecord as DotIcon,
  MoreVert as MoreVertIcon,
  VisibilityOutlined as VisibilityOutlinedIcon,
  AttachMoney as AttachMoneyIcon,
  SwapHoriz as SwapHorizIcon,
  Close as CloseIcon,
  HighlightOff as HighlightOffIcon,
  ContactPhone as ContactPhoneIcon,
} from "@material-ui/icons";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
} from "@material-ui/core";
import QuickActionsMenu from "./QuickActionsMenu";
import TransferTicketModalCustom from "../../components/TransferTicketModalCustom";
import canTransferTicket from "../../helpers/canTransferTicket";
import TicketConversationModal from "../../components/TicketConversationModal";
import KanbanTriggerFlowAction from "../../components/KanbanTriggerFlowAction";
import { useHistory } from "react-router-dom";
import { useContext } from "react";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { format, parseISO } from "date-fns";
import { Draggable } from "react-beautiful-dnd";
import SlaTimer, { getSlaLevel } from "./SlaTimer";

// ── Priority config ──
const PRIORITY_CONFIG = {
  urgent: { icon: "\u{1F534}", label: "Urgente", color: "#F44336" },
  high:   { icon: "\u{1F7E0}", label: "Alta", color: "#FF9800" },
  normal: { icon: "⚪", label: "Normal", color: "#9E9E9E" },
  low:    { icon: "\u{1F535}", label: "Baixa", color: "#2196F3" },
};

// ── Channel icons ──
const CHANNEL_ICONS = {
  whatsapp:  "\u{1F4F1}",
  instagram: "\u{1F4F7}",
  facebook:  "\u{1F310}",
  messenger: "\u{1F4AC}",
  telegram:  "✈️",
  webchat:   "\u{1F5A5}️",
  email:     "✉️",
};

// ── Channel colors (for discrete badge) ──
const CHANNEL_COLORS = {
  whatsapp: "#25D366",
  instagram: "#E1306C",
  facebook: "#4267B2",
  messenger: "#0084FF",
  telegram: "#229ED9",
  webchat: "#0066ff",
  email: "#EA4335",
};

const useStyles = makeStyles((theme) => ({
  // ── SLA pulse keyframes por cor ──
  "@keyframes pulseYellow": {
    "0%, 100%": {
      boxShadow: "0 0 4px 1px rgba(255,193,7,0.25), inset 0 0 0 1px rgba(255,193,7,0.18)",
    },
    "50%": {
      boxShadow: "0 0 14px 3px rgba(255,193,7,0.50), inset 0 0 0 1px rgba(255,193,7,0.35)",
    },
  },
  "@keyframes pulseOrange": {
    "0%, 100%": {
      boxShadow: "0 0 4px 1px rgba(255,152,0,0.30), inset 0 0 0 1px rgba(255,152,0,0.20)",
    },
    "50%": {
      boxShadow: "0 0 16px 4px rgba(255,152,0,0.55), inset 0 0 0 1px rgba(255,152,0,0.40)",
    },
  },
  "@keyframes pulseRed": {
    "0%, 100%": {
      boxShadow: "0 0 5px 2px rgba(244,67,54,0.35), inset 0 0 0 1px rgba(244,67,54,0.25)",
    },
    "50%": {
      boxShadow: "0 0 20px 5px rgba(244,67,54,0.60), inset 0 0 0 1px rgba(244,67,54,0.45)",
    },
  },

  // ══════════════════════════════════════════
  // RICH CARD STYLES (chamativo)
  // ══════════════════════════════════════════
  card: {
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    borderRadius: 10,
    cursor: "grab",
    position: "relative",
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(255,255,255,0.08)"
        : "1px solid rgba(0,0,0,0.06)",
    transition: "box-shadow 0.2s, transform 0.15s, border-color 0.3s",
    "&:hover": {
      boxShadow:
        theme.palette.type === "dark"
          ? "0 4px 16px rgba(0,0,0,0.4)"
          : "0 4px 16px rgba(0,0,0,0.1)",
      transform: "translateY(-1px)",
    },
    backgroundColor:
      theme.palette.type === "dark"
        ? theme.palette.background.paper
        : "#fff",
  },
  cardSlaGreen: {
    borderColor: "#4CAF50 !important",
    boxShadow: "0 0 6px 1px rgba(76,175,80,0.25), inset 0 0 0 1px rgba(76,175,80,0.12)",
  },
  cardSlaYellow: {
    borderColor: "#FFC107 !important",
    animation: "$pulseYellow 3s ease-in-out infinite",
  },
  cardSlaOrange: {
    borderColor: "#FF9800 !important",
    animation: "$pulseOrange 2.5s ease-in-out infinite",
  },
  cardSlaRed: {
    borderColor: "#F44336 !important",
    animation: "$pulseRed 2s ease-in-out infinite",
  },
  cardDragging: {
    boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
    transform: "rotate(2deg)",
  },
  // ── Header: avatar + name block + ticket info ──
  headerRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 6,
  },
  richAvatarWrap: {
    flexShrink: 0,
    position: "relative",
  },
  avatar: {
    width: 38,
    height: 38,
    fontSize: "0.85rem",
    fontWeight: 600,
  },
  richUnreadDot: {
    position: "absolute",
    top: -2,
    right: -2,
    width: 10,
    height: 10,
    borderRadius: "50%",
    backgroundColor: "#4CAF50",
    border: `2px solid ${theme.palette.type === "dark" ? theme.palette.background.paper : "#fff"}`,
  },
  richNameBlock: {
    flex: 1,
    minWidth: 0,
  },
  contactName: {
    fontWeight: 600,
    fontSize: "0.85rem",
    lineHeight: 1.3,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: theme.palette.text.primary,
  },
  richMetaLine: {
    fontSize: "0.68rem",
    color: theme.palette.text.secondary,
    lineHeight: 1.3,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  richTicketInfo: {
    flexShrink: 0,
    textAlign: "right",
  },
  richTicketNum: {
    fontSize: "0.68rem",
    color: theme.palette.text.secondary,
    lineHeight: 1.3,
    whiteSpace: "nowrap",
  },
  richTimestamp: {
    fontSize: "0.68rem",
    color: theme.palette.text.disabled,
    whiteSpace: "nowrap",
  },
  richTimestampUnread: {
    fontSize: "0.68rem",
    color: "#4CAF50",
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
  ticketId: {
    fontSize: "0.72rem",
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
  priorityIcon: {
    fontSize: "0.72rem",
    position: "absolute",
    top: 6,
    right: 6,
  },
  lastMessage: {
    fontSize: "0.78rem",
    color: theme.palette.text.secondary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    lineHeight: 1.4,
    marginBottom: 6,
  },
  // ── Meta: chips (queue, assignee, tags, connection, etc) ──
  metaRow: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 4,
    marginBottom: 6,
  },
  chip: {
    height: 20,
    fontSize: "0.68rem",
    fontWeight: 500,
  },
  tagChip: {
    height: 18,
    fontSize: "0.65rem",
    fontWeight: 500,
  },
  unreadDot: {
    color: "#4CAF50",
    fontSize: 12,
    position: "absolute",
    top: 8,
    right: 24,
  },
  reappearedBadge: {
    fontSize: "0.5rem",
    fontWeight: 700,
    backgroundColor: "#FF5722",
    color: "#fff",
    borderRadius: 3,
    padding: "1px 3px",
    lineHeight: 1.2,
    marginRight: 2,
  },
  // ── Footer: tags/valor + actions ──
  richFooter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    minHeight: 24,
  },
  richFooterLeft: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    flexWrap: "wrap",
  },
  actionsRow: {
    display: "flex",
    alignItems: "center",
    gap: 1,
    flexShrink: 0,
  },
  actionBtn: {
    padding: 3,
    color: theme.palette.text.secondary,
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
      color: theme.palette.text.primary,
    },
    "& svg": { fontSize: 16 },
  },
  actionBtnClose: {
    padding: 3,
    color: theme.palette.error.main,
    "&:hover": {
      backgroundColor: "rgba(244,67,54,0.08)",
    },
    "& svg": { fontSize: 16 },
  },
  timeText: {
    fontSize: "0.68rem",
    color: theme.palette.text.disabled,
    marginLeft: "auto",
  },
  assigneeRow: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    marginTop: theme.spacing(0.5),
  },
  assigneeAvatar: {
    width: 18,
    height: 18,
    fontSize: "0.55rem",
    fontWeight: 600,
  },
  assigneeName: {
    fontSize: "0.72rem",
    color: theme.palette.text.secondary,
  },
  detailRow: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 4,
    marginTop: theme.spacing(0.5),
  },
  valorText: {
    fontSize: "0.72rem",
    fontWeight: 600,
    color: "#2E7D32",
  },
  channelIcon: {
    fontSize: "0.78rem",
  },

  // ══════════════════════════════════════════
  // DISCRETE CARD STYLES (estilo ticket — inspirado no Kanban regular)
  // ══════════════════════════════════════════
  discreteCard: {
    display: "flex",
    flexDirection: "column",
    padding: "10px 12px",
    marginBottom: 6,
    borderRadius: 10,
    cursor: "grab",
    position: "relative",
    borderLeft: "3px solid transparent",
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`,
    backgroundColor:
      theme.palette.type === "dark"
        ? theme.palette.background.paper
        : "#fff",
    boxShadow: theme.palette.type === "dark"
      ? "0 1px 3px rgba(0,0,0,0.3)"
      : "0 1px 3px rgba(0,0,0,0.06)",
    transition: "box-shadow 0.2s, border-color 0.2s",
    "&:hover": {
      boxShadow: theme.palette.type === "dark"
        ? "0 4px 12px rgba(0,0,0,0.4)"
        : "0 4px 12px rgba(0,0,0,0.1)",
    },
  },
  discreteCardDragging: {
    boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
    transform: "rotate(1deg)",
  },
  // ── SLA lateral border (discrete) ──
  discreteSlaLeft: {
    borderLeftWidth: 3,
    borderLeftStyle: "solid",
  },
  discreteSlaLeftGreen: {
    borderLeftColor: "#4CAF50",
  },
  discreteSlaLeftYellow: {
    borderLeftColor: "#FFC107",
  },
  discreteSlaLeftOrange: {
    borderLeftColor: "#FF9800",
  },
  discreteSlaLeftRed: {
    borderLeftColor: "#F44336",
  },
  // ── Header row: avatar + name + ticket nº ──
  discreteHeaderRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 4,
  },
  discreteAvatarWrap: {
    flexShrink: 0,
    position: "relative",
  },
  discreteAvatar: {
    width: 36,
    height: 36,
    fontSize: "0.85rem",
    fontWeight: 600,
  },
  discreteUnreadBadge: {
    "& .MuiBadge-badge": {
      backgroundColor: "#4CAF50",
      color: "#fff",
      fontSize: "0.6rem",
      minWidth: 16,
      height: 16,
      padding: "0 4px",
    },
  },
  discreteUnreadDot: {
    position: "absolute",
    top: -2,
    right: -2,
    width: 10,
    height: 10,
    borderRadius: "50%",
    backgroundColor: "#4CAF50",
    border: `2px solid ${theme.palette.type === "dark" ? theme.palette.background.paper : "#fff"}`,
  },
  discreteNameBlock: {
    flex: 1,
    minWidth: 0,
  },
  discreteName: {
    fontWeight: 600,
    fontSize: "0.82rem",
    lineHeight: 1.3,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: theme.palette.text.primary,
  },
  discreteMetaLine: {
    fontSize: "0.68rem",
    color: theme.palette.text.secondary,
    lineHeight: 1.3,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  discreteTicketInfo: {
    flexShrink: 0,
    textAlign: "right",
  },
  discreteTicketNum: {
    fontSize: "0.68rem",
    color: theme.palette.text.secondary,
    lineHeight: 1.3,
    whiteSpace: "nowrap",
  },
  discreteRespLine: {
    fontSize: "0.65rem",
    color: theme.palette.text.secondary,
    lineHeight: 1.3,
    whiteSpace: "nowrap",
  },
  discreteTime: {
    fontSize: "0.68rem",
    color: theme.palette.text.disabled,
    flexShrink: 0,
    whiteSpace: "nowrap",
  },
  discreteTimeUnread: {
    color: "#4CAF50",
    fontWeight: 600,
  },
  // ── Content ──
  discreteContent: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  discreteLastMsg: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    lineHeight: 1.4,
    marginBottom: 6,
  },
  // ── Footer: tags + actions ──
  discreteFooter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    minHeight: 24,
  },
  discreteTagsWrap: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    flexWrap: "wrap",
  },
  discreteBadgesRow: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 3,
    marginTop: 1,
  },
  discreteBadge: {
    display: "inline-block",
    padding: "1px 6px",
    borderRadius: 3,
    fontSize: "0.6rem",
    fontWeight: 700,
    color: "#fff",
    lineHeight: 1.5,
    textTransform: "uppercase",
    letterSpacing: "0.02em",
    whiteSpace: "nowrap",
  },
  discreteTagsRow: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 3,
    marginTop: 1,
  },
  discreteTag: {
    display: "inline-block",
    padding: "1px 6px",
    borderRadius: 3,
    fontSize: "0.6rem",
    fontWeight: 600,
    color: "#fff",
    lineHeight: 1.5,
    whiteSpace: "nowrap",
  },
  discreteTagOverflow: {
    display: "inline-block",
    padding: "1px 6px",
    borderRadius: 3,
    fontSize: "0.6rem",
    fontWeight: 600,
    color: "#666",
    background: "#e0e0e0",
    lineHeight: 1.5,
    whiteSpace: "nowrap",
  },
  // ── Actions: ALWAYS visible ──
  discreteActions: {
    display: "flex",
    alignItems: "center",
    gap: 1,
    flexShrink: 0,
  },
  discreteActionBtn: {
    padding: 3,
    color: theme.palette.text.secondary,
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
      color: theme.palette.text.primary,
    },
    "& svg": { fontSize: 16 },
  },
  discreteActionBtnClose: {
    padding: 3,
    color: theme.palette.error.main,
    "&:hover": {
      backgroundColor: "rgba(244,67,54,0.08)",
    },
    "& svg": { fontSize: 16 },
  },
  discreteSlaIndicator: {
    position: "absolute",
    bottom: -1,
    right: -1,
    width: 10,
    height: 10,
    borderRadius: "50%",
    border: `2px solid ${theme.palette.type === "dark" ? theme.palette.background.paper : "#fff"}`,
  },
  discretePriorityDot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    flexShrink: 0,
  },
  discreteDetailRow: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 4,
    marginTop: 1,
  },
  discreteValor: {
    fontSize: "0.68rem",
    fontWeight: 600,
    color: "#2E7D32",
    backgroundColor: "rgba(76,175,80,0.1)",
    padding: "1px 6px",
    borderRadius: 4,
    whiteSpace: "nowrap",
  },
  discreteChannelIcon: {
    fontSize: "0.72rem",
  },
}));

const getInitials = (name) => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return parts.length > 1
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : parts[0].substring(0, 2).toUpperCase();
};

const formatTime = (dateStr) => {
  if (!dateStr) return "";
  try {
    const d = typeof dateStr === "string" ? parseISO(dateStr) : new Date(dateStr);
    const now = new Date();
    const diffMs = now - d;
    if (diffMs < 60000) return "agora";
    if (diffMs < 3600000) return `${Math.floor(diffMs / 60000)}min`;
    if (diffMs < 86400000) return format(d, "HH:mm");
    return format(d, "dd/MM");
  } catch {
    return "";
  }
};

const formatCurrency = (value) => {
  if (value == null || value === 0) return null;
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
};

/**
 * KanbanEnterpriseCard — suporta 2 estilos visuais e 3 view modes.
 *
 * cardStyle:
 *  - "rich"     : card chamativo com SLA pulse, chips, ícones grandes
 *  - "discrete" : estilo ticket sidebar — compacto, badges coloridos, lista
 *
 * viewMode:
 *  - "compact":     avatar, nome, tempo, SLA dot, priority
 *  - "comfortable": + lastMessage, queue, assignee, tags
 *  - "detailed":    + connection, channel, valor, wallet
 */
const COMPACT_FIELDS = ["name", "sla", "priority"];
const COMFORTABLE_FIELDS = ["name", "lastMessage", "queue", "assignee", "sla", "tags"];
const DETAILED_FIELDS = ["name", "lastMessage", "queue", "assignee", "sla", "tags", "connection", "wallet", "valor", "priority"];

const getFieldsForMode = (mode) => {
  if (mode === "compact") return COMPACT_FIELDS;
  if (mode === "detailed") return DETAILED_FIELDS;
  return COMFORTABLE_FIELDS;
};

// ── SLA colors ──
const SLA_COLORS = {
  green: "#4CAF50",
  yellow: "#FFC107",
  orange: "#FF9800",
  red: "#F44336",
};

const KanbanEnterpriseCard = memo(
  ({ ticket, index, onHover, onLeave, viewMode = "comfortable", cardStyle = "rich", slaConfig, visibleFields, onQuickAction, isAdmin }) => {
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [transferModalOpen, setTransferModalOpen] = useState(false);
    const [conversationModalOpen, setConversationModalOpen] = useState(false);
    const [valueDialogOpen, setValueDialogOpen] = useState(false);
    const [newValue, setNewValue] = useState("");
    const [closing, setClosing] = useState(false);

    const { user } = useContext(AuthContext);

    // visibleFields from column settings override viewMode defaults
    const fields = visibleFields || getFieldsForMode(viewMode);
    const classes = useStyles();
    const history = useHistory();

    const contact = ticket?.contact || {};
    const queue = ticket?.queue;
    const assignee = ticket?.user;
    const whatsapp = ticket?.whatsapp;
    const tags = ticket?.tags || [];
    const unread = (ticket?.unreadMessages || 0) > 0;
    const reappearedInChat = unread && ticket?.kanbanColumnId && !ticket?.kanbanHiddenFromChat;
    const priority = ticket?.priority || "normal";
    const priorityCfg = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG.normal;
    const channel = ticket?.channel || "whatsapp";
    const channelIcon = CHANNEL_ICONS[channel] || CHANNEL_ICONS.whatsapp;
    const channelColor = CHANNEL_COLORS[channel] || CHANNEL_COLORS.whatsapp;
    const valor = formatCurrency(ticket?.valorVenda);

    // Quando visibleFields vem do settingsJson da coluna, ele é a autoridade.
    // viewMode só restringe quando visibleFields NÃO foi configurado.
    const hasColumnOverride = !!visibleFields;
    const isCompact = !hasColumnOverride && viewMode === "compact";

    // Wallet info (from contact.ContactWallets)
    const wallets = contact.ContactWallets || [];
    const walletUser = wallets.length > 0 ? wallets[0]?.user : null;

    // ── SLA alert glow (atualiza a cada 60s) ──
    const [slaTick, setSlaTick] = useState(() => Date.now());
    useEffect(() => {
      if (!ticket?.enteredLaneAt) return;
      const interval = setInterval(() => setSlaTick(Date.now()), 60000);
      return () => clearInterval(interval);
    }, [ticket?.enteredLaneAt]);

    const slaLevel = useMemo(() => {
      if (!ticket?.enteredLaneAt) return "green";
      const start = new Date(ticket.enteredLaneAt).getTime();
      if (!start || isNaN(start)) return "green";
      const elapsedMin = (slaTick - start) / 60000;
      return getSlaLevel(elapsedMin, slaConfig);
    }, [ticket?.enteredLaneAt, slaConfig, slaTick]);

    // Classe de glow pulsante por nível (só para rich)
    const slaGlowClass =
      cardStyle === "rich" ? (
        slaLevel === "red" ? classes.cardSlaRed :
        slaLevel === "orange" ? classes.cardSlaOrange :
        slaLevel === "yellow" ? classes.cardSlaYellow :
        slaLevel === "green" && ticket?.enteredLaneAt ? classes.cardSlaGreen :
        ""
      ) : "";

    const handleOpenTicket = useCallback(() => {
      if (ticket?.uuid) {
        history.push(`/tickets/${ticket.uuid}`);
      }
    }, [ticket?.uuid, history]);

    const canViewContent = user?.profile === "admin" || ticket?.userId === user?.id;

    // ── Valor da oportunidade (extraInfo) ──
    const customFields = contact?.extraInfo || [];
    const valueFieldIndex = customFields.findIndex(f => f.name === "valor");
    const valueField = valueFieldIndex !== -1 ? customFields[valueFieldIndex] : null;
    const opportunityValue = valueField ? parseFloat(valueField.value) : null;
    const hasOpportunityValue = Boolean(valueField) && String(valueField.value ?? "").trim() !== "" && Number.isFinite(opportunityValue);

    const fetchContactExtraInfo = useCallback(async (contactId) => {
      const { data } = await api.get(`/contacts/${contactId}`);
      return Array.isArray(data?.extraInfo) ? data.extraInfo : [];
    }, []);

    const handleOpenValueDialog = useCallback((e) => {
      e.stopPropagation();
      setNewValue(valueField ? valueField.value.toString() : "");
      setValueDialogOpen(true);
    }, [valueField]);

    const handleSaveValue = useCallback(async () => {
      try {
        const currentExtraInfo = await fetchContactExtraInfo(contact.id);
        const preserved = currentExtraInfo.filter(f => f?.name !== "valor");
        await api.put(`/contacts/${contact.id}`, {
          extraInfo: [...preserved, { name: "valor", value: newValue }],
        });
        setValueDialogOpen(false);
      } catch (err) {
        toastError(err);
      }
    }, [contact.id, newValue, fetchContactExtraInfo]);

    const handleRemoveValue = useCallback(async (e) => {
      e.stopPropagation();
      try {
        const currentExtraInfo = await fetchContactExtraInfo(contact.id);
        const preserved = currentExtraInfo.filter(f => f?.name !== "valor");
        await api.put(`/contacts/${contact.id}`, { extraInfo: preserved });
      } catch (err) {
        toastError(err);
      }
    }, [contact.id, fetchContactExtraInfo]);

    const handleCloseTicket = useCallback(async (e) => {
      e.stopPropagation();
      if (closing) return;
      setClosing(true);
      try {
        await api.put(`/tickets/${ticket.id}`, {
          status: "closed",
          userId: user?.id || null,
          queueId: ticket?.queue?.id || ticket?.queueId || null,
          sendFarewellMessage: false,
        });
      } catch (err) {
        toastError(err);
      } finally {
        setClosing(false);
      }
    }, [ticket?.id, ticket?.queue?.id, ticket?.queueId, user?.id, closing]);

    const handleOpenTransfer = useCallback((e) => {
      e.stopPropagation();
      setTransferModalOpen(true);
    }, []);

    const handleOpenConversation = useCallback((e) => {
      e.stopPropagation();
      setConversationModalOpen(true);
    }, []);

    // ══════════════════════════════════════════
    // DISCRETE CARD RENDER
    // ══════════════════════════════════════════
    // ── SLA left border class (discrete) ──
    const discreteSlaClass = ticket?.enteredLaneAt
      ? `${classes.discreteSlaLeft} ${
          slaLevel === "red" ? classes.discreteSlaLeftRed :
          slaLevel === "orange" ? classes.discreteSlaLeftOrange :
          slaLevel === "yellow" ? classes.discreteSlaLeftYellow :
          classes.discreteSlaLeftGreen
        }`
      : "";

    if (cardStyle === "discrete") {
      const slaBorderColor = SLA_COLORS[slaLevel] || SLA_COLORS.green;

      return (
        <Draggable draggableId={String(ticket.id)} index={index} type="CARD">
          {(provided, snapshot) => (
            <Paper
              ref={provided.innerRef}
              {...provided.draggableProps}
              {...provided.dragHandleProps}
              className={`${classes.discreteCard} ${
                snapshot.isDragging ? classes.discreteCardDragging : ""
              }`}
              style={{
                ...provided.draggableProps.style,
                borderLeftColor: slaBorderColor,
              }}
              elevation={0}
            >
              {/* ── Header: Avatar + Name/Meta + Ticket nº/Resp ── */}
              <div className={classes.discreteHeaderRow}>
                <div className={classes.discreteAvatarWrap}>
                  <Avatar
                    className={classes.discreteAvatar}
                    src={contact.urlPicture || undefined}
                    style={{ backgroundColor: queue?.color || "#9E9E9E" }}
                  >
                    {getInitials(contact.name)}
                  </Avatar>
                  {unread && <span className={classes.discreteUnreadDot} />}
                  {fields.includes("sla") && ticket?.enteredLaneAt && (
                    <span
                      className={classes.discreteSlaIndicator}
                      style={{ backgroundColor: SLA_COLORS[slaLevel] || SLA_COLORS.green }}
                      title={`SLA: ${slaLevel}`}
                    />
                  )}
                </div>

                <div className={classes.discreteNameBlock}>
                  <Typography className={classes.discreteName}>
                    {fields.includes("priority") && priority !== "normal" && (
                      <span
                        className={classes.discretePriorityDot}
                        style={{ backgroundColor: priorityCfg.color, display: "inline-block", marginRight: 4, verticalAlign: "middle" }}
                      />
                    )}
                    {contact.name || contact.number || "Sem nome"}
                  </Typography>
                  <Typography className={classes.discreteMetaLine}>
                    {assignee ? `Resp: ${assignee.name}` : "Sem atendente"}
                    {queue ? ` · ${queue.name}` : ""}
                  </Typography>
                </div>

                <div className={classes.discreteTicketInfo}>
                  <Typography className={classes.discreteTicketNum}>
                    Ticket nº {ticket.id}
                  </Typography>
                  <Typography className={`${classes.discreteTime} ${unread ? classes.discreteTimeUnread : ""}`}>
                    {formatTime(ticket.updatedAt)}
                  </Typography>
                </div>
              </div>

              {/* ── Last message ── */}
              {ticket.lastMessage && (
                <Typography className={classes.discreteLastMsg}>
                  {ticket.lastMessage}
                </Typography>
              )}

              {/* ── Footer: Tags/Badges + Action Icons ── */}
              <div className={classes.discreteFooter}>
                <div className={classes.discreteTagsWrap}>
                  {/* Connection badge */}
                  {fields.includes("connection") && whatsapp && (
                    <span
                      className={classes.discreteBadge}
                      style={{ backgroundColor: whatsapp.color || channelColor }}
                    >
                      {whatsapp.name}
                    </span>
                  )}
                  {/* Valor */}
                  {fields.includes("valor") && valor && (
                    <span className={classes.discreteValor}>{valor}</span>
                  )}
                  {/* Tags */}
                  {fields.includes("tags") && tags.slice(0, 3).map((tag) => (
                    <span
                      key={tag.id}
                      className={classes.discreteTag}
                      style={{ backgroundColor: tag.color || "#E0E0E0" }}
                    >
                      {tag.name}
                    </span>
                  ))}
                  {fields.includes("tags") && tags.length > 3 && (
                    <span className={classes.discreteTagOverflow}>
                      +{tags.length - 3}
                    </span>
                  )}
                  {/* SLA timer inline */}
                  {fields.includes("sla") && ticket?.enteredLaneAt && (
                    <SlaTimer
                      enteredLaneAt={ticket.enteredLaneAt}
                      slaConfig={slaConfig}
                      dotOnly={isCompact}
                    />
                  )}
                </div>

                {/* Action icons — ALWAYS visible (igual Kanban legacy) */}
                <div className={classes.discreteActions}>
                  {reappearedInChat && (
                    <span
                      style={{
                        fontSize: "0.5rem",
                        fontWeight: 700,
                        backgroundColor: "#FF5722",
                        color: "#fff",
                        borderRadius: 3,
                        padding: "1px 3px",
                        lineHeight: 1.2,
                        marginRight: 2,
                      }}
                    >
                      Nova
                    </span>
                  )}
                  {canViewContent && (
                    <Tooltip title="Espiar" placement="top">
                      <IconButton size="small" className={classes.discreteActionBtn} onClick={handleOpenConversation}>
                        <VisibilityOutlinedIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  <KanbanTriggerFlowAction
                    ticket={ticket}
                    className={classes.discreteActionBtn}
                  />
                  <Tooltip title={hasOpportunityValue ? `R$ ${opportunityValue.toFixed(2)}` : "Valor"} placement="top">
                    <IconButton
                      size="small"
                      className={classes.discreteActionBtn}
                      style={hasOpportunityValue ? { color: "#4CAF50" } : undefined}
                      onClick={handleOpenValueDialog}
                    >
                      <AttachMoneyIcon />
                    </IconButton>
                  </Tooltip>
                  {hasOpportunityValue && (
                    <Tooltip title="Remover valor" placement="top">
                      <IconButton size="small" className={classes.discreteActionBtnClose} onClick={handleRemoveValue}>
                        <HighlightOffIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  {ticket.status !== "closed" && (
                    <>
                      {canTransferTicket(user) && (
                        <Tooltip title="Transferir" placement="top">
                          <IconButton size="small" className={classes.discreteActionBtn} onClick={handleOpenTransfer}>
                            <SwapHorizIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Fechar" placement="top">
                        <IconButton size="small" className={classes.discreteActionBtnClose} onClick={handleCloseTicket} disabled={closing}>
                          <CloseIcon />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                  <Tooltip title="Ações rápidas" placement="top">
                    <IconButton
                      size="small"
                      className={classes.discreteActionBtn}
                      onClick={(e) => {
                        e.stopPropagation();
                        setMenuAnchor(e.currentTarget);
                      }}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Tooltip>
                </div>
              </div>

              {/* Quick Actions Menu (shared) */}
              <QuickActionsMenu
                ticket={ticket}
                anchorEl={menuAnchor}
                onClose={() => setMenuAnchor(null)}
                isAdmin={isAdmin}
                onChangePriority={(id, p) => {
                  onQuickAction && onQuickAction("priority", id, { priority: p });
                }}
                onChangeAssignee={(id) => {
                  onQuickAction && onQuickAction("assignee", id);
                }}
                onChangeQueue={(id) => {
                  onQuickAction && onQuickAction("queue", id);
                }}
                onChangeConnection={(id) => {
                  onQuickAction && onQuickAction("connection", id);
                }}
                onViewHistory={(id) => {
                  onQuickAction && onQuickAction("history", id);
                }}
              />

              {/* ── Modais (igual Kanban legacy) ── */}
              <Dialog open={valueDialogOpen} onClose={() => setValueDialogOpen(false)}>
                <DialogTitle>{valueField ? "Editar" : "Atribuir"} Valor da Oportunidade</DialogTitle>
                <DialogContent>
                  <TextField
                    label="Valor"
                    type="number"
                    fullWidth
                    value={newValue}
                    onChange={(e) => setNewValue(e.target.value)}
                    variant="outlined"
                    size="small"
                    autoFocus
                  />
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setValueDialogOpen(false)} color="secondary" variant="outlined">
                    Cancelar
                  </Button>
                  <Button onClick={handleSaveValue} color="primary" variant="contained">
                    Salvar
                  </Button>
                </DialogActions>
              </Dialog>

              {canTransferTicket(user) && transferModalOpen && (
                <TransferTicketModalCustom
                  modalOpen={transferModalOpen}
                  onClose={() => setTransferModalOpen(false)}
                  ticketid={ticket.id}
                  ticket={ticket}
                />
              )}

              {conversationModalOpen && (
                <TicketConversationModal
                  open={conversationModalOpen}
                  onClose={() => setConversationModalOpen(false)}
                  ticket={ticket}
                />
              )}
            </Paper>
          )}
        </Draggable>
      );
    }

    // ══════════════════════════════════════════
    // RICH CARD RENDER (chamativo — mesma estrutura do discreto + recursos ricos)
    // ══════════════════════════════════════════
    const richSlaBorderColor = SLA_COLORS[slaLevel] || SLA_COLORS.green;

    return (
      <Draggable draggableId={String(ticket.id)} index={index} type="CARD">
        {(provided, snapshot) => (
          <Paper
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            className={`${classes.card} ${snapshot.isDragging ? classes.cardDragging : ""} ${slaGlowClass}`}
            style={{
              ...provided.draggableProps.style,
              borderLeft: `3px solid ${richSlaBorderColor}`,
            }}
            elevation={0}
          >
            {/* ── Header: Avatar + Name/Meta + Ticket nº/Time ── */}
            <div className={classes.headerRow}>
              <div className={classes.richAvatarWrap}>
                <Avatar
                  className={classes.avatar}
                  src={contact.urlPicture || undefined}
                  style={{ backgroundColor: queue?.color || "#9E9E9E" }}
                >
                  {getInitials(contact.name)}
                </Avatar>
                {unread && <span className={classes.richUnreadDot} />}
              </div>

              <div className={classes.richNameBlock}>
                <Typography className={classes.contactName}>
                  {fields.includes("priority") && priority !== "normal" && (
                    <span style={{ marginRight: 4, verticalAlign: "middle" }}>
                      {priorityCfg.icon}
                    </span>
                  )}
                  {contact.name || contact.number || "Sem nome"}
                </Typography>
                <Typography className={classes.richMetaLine}>
                  {assignee ? `Resp: ${assignee.name}` : "Sem atendente"}
                  {queue ? ` · ${queue.name}` : ""}
                </Typography>
              </div>

              <div className={classes.richTicketInfo}>
                <Typography className={classes.richTicketNum}>
                  Ticket nº {ticket.id}
                </Typography>
                <Typography className={unread ? classes.richTimestampUnread : classes.richTimestamp}>
                  {formatTime(ticket.updatedAt)}
                </Typography>
              </div>
            </div>

            {/* ── Last message ── */}
            {ticket.lastMessage && (
              <Typography className={classes.lastMessage}>
                {ticket.lastMessage}
              </Typography>
            )}

            {/* ── Meta row: chips ricos (connection, SLA, tags, wallet, priority, valor) ── */}
            <div className={classes.metaRow}>
              {/* Conexão chip */}
              {fields.includes("connection") && whatsapp && (
                <Chip
                  className={classes.chip}
                  label={whatsapp.name}
                  size="small"
                  style={{
                    backgroundColor: whatsapp.color || channelColor,
                    color: "#fff",
                  }}
                />
              )}
              {/* SLA timer */}
              {fields.includes("sla") && (
                <SlaTimer
                  enteredLaneAt={ticket.enteredLaneAt}
                  slaConfig={slaConfig}
                />
              )}
              {/* Tags */}
              {fields.includes("tags") && tags.slice(0, 3).map((tag) => (
                <Chip
                  key={tag.id}
                  className={classes.tagChip}
                  label={tag.name}
                  size="small"
                  style={{
                    backgroundColor: tag.color || "#E0E0E0",
                    color: "#fff",
                  }}
                />
              ))}
              {fields.includes("tags") && tags.length > 3 && (
                <Chip
                  className={classes.tagChip}
                  label={`+${tags.length - 3}`}
                  size="small"
                  variant="outlined"
                />
              )}
              {/* Carteira */}
              {fields.includes("wallet") && walletUser && (
                <Chip
                  className={classes.chip}
                  label={walletUser.name}
                  size="small"
                  variant="outlined"
                  icon={<span style={{ fontSize: "0.7rem" }}>{"\u{1F4BC}"}</span>}
                />
              )}
              {/* Priority badge (detailed mode) */}
              {fields.includes("priority") && priority !== "normal" && (
                <Chip
                  className={classes.chip}
                  label={`${priorityCfg.icon} ${priorityCfg.label}`}
                  size="small"
                  style={{
                    borderColor: priorityCfg.color,
                    color: priorityCfg.color,
                  }}
                  variant="outlined"
                />
              )}
            </div>

            {/* ── Footer: valor + action icons (ALWAYS visible) ── */}
            <div className={classes.richFooter}>
              <div className={classes.richFooterLeft}>
                {/* Valor */}
                {fields.includes("valor") && valor && (
                  <span className={classes.valorText}>{valor}</span>
                )}
                {/* Canal icon */}
                {fields.includes("connection") && (
                  <Tooltip title={`Canal: ${channel}`}>
                    <span style={{ fontSize: "0.72rem" }}>{channelIcon}</span>
                  </Tooltip>
                )}
              </div>

              {/* Action icons — ALWAYS visible (igual Kanban legacy) */}
              <div className={classes.actionsRow}>
                {reappearedInChat && (
                  <span className={classes.reappearedBadge}>Nova</span>
                )}
                {canViewContent && (
                  <Tooltip title="Espiar" placement="top">
                    <IconButton size="small" className={classes.actionBtn} onClick={handleOpenConversation}>
                      <VisibilityOutlinedIcon />
                    </IconButton>
                  </Tooltip>
                )}
                <KanbanTriggerFlowAction ticket={ticket} className={classes.actionBtn} />
                <Tooltip title={hasOpportunityValue ? `R$ ${opportunityValue.toFixed(2)}` : "Valor"} placement="top">
                  <IconButton
                    size="small"
                    className={classes.actionBtn}
                    style={hasOpportunityValue ? { color: "#4CAF50" } : undefined}
                    onClick={handleOpenValueDialog}
                  >
                    <AttachMoneyIcon />
                  </IconButton>
                </Tooltip>
                {hasOpportunityValue && (
                  <Tooltip title="Remover valor" placement="top">
                    <IconButton size="small" className={classes.actionBtnClose} onClick={handleRemoveValue}>
                      <HighlightOffIcon />
                    </IconButton>
                  </Tooltip>
                )}
                {ticket.status !== "closed" && (
                  <>
                    {canTransferTicket(user) && (
                      <Tooltip title="Transferir" placement="top">
                        <IconButton size="small" className={classes.actionBtn} onClick={handleOpenTransfer}>
                          <SwapHorizIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Fechar" placement="top">
                      <IconButton size="small" className={classes.actionBtnClose} onClick={handleCloseTicket} disabled={closing}>
                        <CloseIcon />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
                <Tooltip title="Ações rápidas" placement="top">
                  <IconButton
                    size="small"
                    className={classes.actionBtn}
                    onClick={(e) => {
                      e.stopPropagation();
                      setMenuAnchor(e.currentTarget);
                    }}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </Tooltip>
              </div>
            </div>

            {/* Quick Actions Menu */}
            <QuickActionsMenu
              ticket={ticket}
              anchorEl={menuAnchor}
              onClose={() => setMenuAnchor(null)}
              isAdmin={isAdmin}
              onChangePriority={(id, p) => {
                onQuickAction && onQuickAction("priority", id, { priority: p });
              }}
              onChangeAssignee={(id) => {
                onQuickAction && onQuickAction("assignee", id);
              }}
              onChangeQueue={(id) => {
                onQuickAction && onQuickAction("queue", id);
              }}
              onChangeConnection={(id) => {
                onQuickAction && onQuickAction("connection", id);
              }}
              onViewHistory={(id) => {
                onQuickAction && onQuickAction("history", id);
              }}
            />

            {/* ── Modais ── */}
            <Dialog open={valueDialogOpen} onClose={() => setValueDialogOpen(false)}>
              <DialogTitle>{valueField ? "Editar" : "Atribuir"} Valor da Oportunidade</DialogTitle>
              <DialogContent>
                <TextField
                  label="Valor"
                  type="number"
                  fullWidth
                  value={newValue}
                  onChange={(e) => setNewValue(e.target.value)}
                  variant="outlined"
                  size="small"
                  autoFocus
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setValueDialogOpen(false)} color="secondary" variant="outlined">
                  Cancelar
                </Button>
                <Button onClick={handleSaveValue} color="primary" variant="contained">
                  Salvar
                </Button>
              </DialogActions>
            </Dialog>

            {canTransferTicket(user) && transferModalOpen && (
              <TransferTicketModalCustom
                modalOpen={transferModalOpen}
                onClose={() => setTransferModalOpen(false)}
                ticketid={ticket.id}
                ticket={ticket}
              />
            )}

            {conversationModalOpen && (
              <TicketConversationModal
                open={conversationModalOpen}
                onClose={() => setConversationModalOpen(false)}
                ticket={ticket}
              />
            )}
          </Paper>
        )}
      </Draggable>
    );
  },
  (prev, next) => {
    return (
      prev.ticket.id === next.ticket.id &&
      prev.ticket.updatedAt === next.ticket.updatedAt &&
      prev.ticket.status === next.ticket.status &&
      prev.ticket.queueId === next.ticket.queueId &&
      prev.ticket.userId === next.ticket.userId &&
      prev.ticket.unreadMessages === next.ticket.unreadMessages &&
      prev.ticket.lastMessage === next.ticket.lastMessage &&
      prev.ticket.priority === next.ticket.priority &&
      prev.ticket.enteredLaneAt === next.ticket.enteredLaneAt &&
      prev.ticket.valorVenda === next.ticket.valorVenda &&
      prev.ticket.whatsappId === next.ticket.whatsappId &&
      prev.ticket.kanbanHiddenFromChat === next.ticket.kanbanHiddenFromChat &&
      prev.viewMode === next.viewMode &&
      prev.cardStyle === next.cardStyle &&
      prev.index === next.index
    );
  }
);

KanbanEnterpriseCard.displayName = "KanbanEnterpriseCard";

export default KanbanEnterpriseCard;
